import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Component } from "react";
import { IndividualWalletCloseRequest, MerchantWalletCloseRequest, Wallets } from "../../models/FinancialData";
import { AppCard, StatusBadge, LoadingDots } from "../../components/ui";
import { dateBodyTemplate } from "../../utils/columnTemplates";
import { merchantWalletApiService } from "../../services/api/merchants/MerchantWalletApiService";
import { toastService } from "../../services/toastService";
import { userWalletApiService } from "../../services/api/users/UserWalletApiService";
import { confirmDanger } from "../../utils/confirmHelpers";
import { IndividualStatus } from "../../models/User";
import { X } from "lucide-react";

interface MerchantWalletsProps {
    Id: number | undefined;
    walletType: WalletType;
    cbsId: number | string | undefined;
    /**
     * The individual's status. Not a gate - it is only a change signal, so the card re-reads when
     * an operator activates the customer on the same page. Merchants leave it undefined.
     */
    individualStatus?: IndividualStatus;
}

interface MerchantWalletsState {
    wallets: Wallets[];
    isLoading: boolean;
    isWalletBalanceLoading: boolean;
}

export enum WalletType {
    User,
    Merchant,
}

export default class MerchantWalletsComponent extends Component<MerchantWalletsProps, MerchantWalletsState> {
    constructor(props: MerchantWalletsProps | Readonly<MerchantWalletsProps>) {
        super(props);
        this.state = {
            wallets: [],
            isLoading: true,
            isWalletBalanceLoading: true,
        };
    }

    async componentDidMount() {
        await this.loadWallets();
    }

    async componentDidUpdate(prevProps: MerchantWalletsProps) {
        // Activating a customer re-fetches the profile and pushes a new status down, but the card
        // was mounted once and never looked again - so it kept asserting the customer was not
        // onboarded until a full page reload.
        if (prevProps.individualStatus !== this.props.individualStatus || prevProps.cbsId !== this.props.cbsId) {
            await this.loadWallets();
        }
    }

    /**
     * No status rule here, deliberately. Inferring "has no wallets" from a status is the mistake
     * that produced this ticket: a customer can move Active -> New (Individual.cs permits it, as it
     * does Deactivated -> New and Closed -> New), so a pre-onboarding status does not mean the
     * customer never held wallets. The back-office endpoint answers with an empty list when there
     * is genuinely nothing, so the card can just ask and show what comes back.
     */
    private static readonly EmptyWalletsMessage = "No wallets yet";

    loadWallets = async () => {
        if (this.props.Id == undefined || this.props.cbsId == undefined || this.props.cbsId == "") {
            this.setState({ isLoading: false });
            return;
        }

        this.setState({ isLoading: true });

        const walletsResult =
            this.props.walletType == WalletType.Merchant
                ? await merchantWalletApiService.getWallets(this.props.Id)
                : await userWalletApiService.getBackOfficeWallets(this.props.Id);

        if (!walletsResult.success) {
            this.setState({ isLoading: false });
            console.error("Failed to load wallets", { id: this.props.Id, walletType: this.props.walletType, message: walletsResult.message });
            return;
        }

        this.setState({ isLoading: false, wallets: walletsResult.data });

        let wallets = walletsResult.data;

        const balanceResult = (
            await Promise.all(
                wallets.map((item) =>
                    this.props.walletType == WalletType.Merchant
                        ? merchantWalletApiService.getWalletBalance({ merchantId: this.props.Id!, accountNumber: item.id })
                        : userWalletApiService.getWalletBalance({ individualId: this.props.Id!, accountNumber: item.id }),
                ),
            )
        )
            .filter((a) => a.success)
            .map((a) => a.data);

        wallets = wallets.map((w) => {
            w.balance = balanceResult.find((a) => a.id == w.id)?.balance ?? 0;
            w.blockedAmount = balanceResult.find((a) => a.id == w.id)?.blockedAmount ?? 0;
            w.cbsId = balanceResult.find((a) => a.id == w.id)?.cbsId ?? "";

            return w;
        });

        this.setState({ wallets: wallets, isWalletBalanceLoading: false });
    };

    actionBodyTemplate = (rowData: Wallets) => {
        return (
            <button
                type="button"
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-red-500 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20 dark:hover:text-red-400 transition-colors"
                tabIndex={0}
                onClick={() => this.confirmCloseWalletMethod(rowData)}
                title="Close Wallet"
            >
                <X size={16} strokeWidth={2} />
            </button>
        );
    };

    closeWalletMethod = async (item: Wallets) => {
        const modal =
            this.props.walletType === WalletType.Merchant
                ? { merchantId: this.props.Id!, walletId: item.id }
                : { individualId: this.props.Id!, walletId: item.id };

        const walletsResult =
            this.props.walletType === WalletType.Merchant
                ? await merchantWalletApiService.closeWallet(modal as MerchantWalletCloseRequest)
                : await userWalletApiService.closeWallet(modal as IndividualWalletCloseRequest);

        if (!walletsResult.success) {
            this.setState({ isLoading: false });
            toastService.error(
                walletsResult.message.includes("Close Mode cannot be blank")
                    ? "A wallet balance should be zero before closing it."
                    : walletsResult.message,
            );
            return;
        }
        toastService.success("Wallet closed", "Action Completed");
        await this.loadWallets();
    };

    confirmCloseWalletMethod = async (item: Wallets) => {
        confirmDanger(
            "This action will close the wallet. Are you sure you want to proceed?",
            "Close Wallet",
            () => this.closeWalletMethod(item),
        );
    };

    render() {
        if (this.state.isLoading) {
            return (
                <AppCard>
                    <AppCard.Header title="Wallet Info" />
                    <AppCard.Body>
                        <LoadingDots />
                    </AppCard.Body>
                </AppCard>
            );
        }

        return (
            <AppCard>
                <AppCard.Header title="Wallet Info" />
                <AppCard.Body className="!p-0">
                    <DataTable value={this.state.wallets} loading={this.state.isLoading} emptyMessage={MerchantWalletsComponent.EmptyWalletsMessage}>
                        <Column field="id" header="Id"></Column>
                        <Column field="cbsId" header="CBS ID"></Column>
                        <Column field="ibanAccountNumber" header="IBAN Account Number"></Column>
                        <Column field="publicToken" header="Public Token"></Column>
                        <Column
                            field="default"
                            header="Default"
                            body={(rowData: Wallets) => StatusBadge(rowData.default, "Yes", "No")}
                        ></Column>
                        <Column field="tariff" header="Tariff"></Column>
                        <Column
                            field="balance"
                            header="Balance"
                            body={(rowData: Wallets) =>
                                this.state.isWalletBalanceLoading ? (
                                    <LoadingDots />
                                ) : rowData.balance
                            }
                        ></Column>
                        <Column field="currency" header="Currency"></Column>
                        <Column
                            field="blockedAmount"
                            header="Blocked Amount"
                            body={(rowData: Wallets) =>
                                this.state.isWalletBalanceLoading ? (
                                    <LoadingDots />
                                ) : rowData.blockedAmount
                            }
                        ></Column>
                        <Column
                            field="timestamp"
                            header="Creation TimeStamp"
                            body={dateBodyTemplate("timestamp", "dddd, MMM DD, YYYY")}
                        ></Column>
                        <Column
                            header="Close Wallet"
                            body={this.actionBodyTemplate}
                            headerStyle={{ width: "8em", textAlign: "center" }}
                            bodyStyle={{ textAlign: "center", overflow: "visible" }}
                        />
                    </DataTable>
                </AppCard.Body>
            </AppCard>
        );
    }
}
